import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";
import CancelButton from "components/CancelButton";
import ConfirmBoxModal from "components/ConfirmBoxModal";
import {
  addStateOperationsGroup,
  editStateOperationsGroup,
  rowEditOperationsGroup,
  operationsGroupFetching,
} from "../atoms/atoms";
import { IOperationsGroup } from "../types";
import useEditOperationsGroup from "../hooks/useEditOperationsGroup";
import useAddOperationsGroup from "../hooks/useAddOperationsGroup";
import SubmitHookForm from "components/SubmitHookForm";
import OperationsGroupDetails from "./OperationsGroupDetails";

export const OperationsGroupForm = (props: {
  setRecent: React.Dispatch<React.SetStateAction<string>>;
}) => {
  // Drilled Props
  const { setRecent } = props;

  // Atom States
  const [isAddState, setIsAddState] = useRecoilState(addStateOperationsGroup);
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateOperationsGroup
  );
  const [editData, setEditData] = useRecoilState(rowEditOperationsGroup);
  const setIsFetching = useSetRecoilState(operationsGroupFetching);

  // Local States
  const [newData, setNewData] = useState<IOperationsGroup>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Hook Functions
  const updateData = useEditOperationsGroup();
  const addData = useAddOperationsGroup();

  // Form Functions
  const useFormMethods = useForm({
    defaultValues: { ...editData },
  });

  const onSubmit = (data) => {
    const value = {
      ...data,
      operationName: data.operationName,
    };
    setNewData(value);
    setSaveModal(true);
    setChanges(false);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setActionEditState(false);
  };

  const handleSave = async () => {
    if (newData) {
      if (actionEditState) {
        updateData(newData);
      } else {
        addData(newData);
      }
    }

    setRecent("");
    setIsFetching(true);
    setIsSaving(false);
    setEditData(undefined);
    useFormMethods.reset();
    useFormMethods.clearErrors();
  };

  return (
    <>
      <div className="p-2 space-y-2 border-l-2 border-primary shadow-lg">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState
                  ? "Add New Operations Group"
                  : "Edit Operations Group"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 mt-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal my-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <OperationsGroupDetails data={editData} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={
            isAddState ? "Add Operations Group" : "Update Operations Group"
          }
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
            setChanges(true);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default OperationsGroupForm;
